import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, Mail, Globe, MessageSquare, Phone, UserCheck } from 'lucide-react';

const CRSPage = () => {
  const ticketCategories = [
    { name: 'Healthcare', percentage: 30, color: '#EA580C', description: 'Medicine shortages, ambulance delays, clinic sanitation' },
    { name: 'Construction & Infrastructure', percentage: 25, color: '#1E3A8A', description: 'Potholes, illegal construction, streetlights' },
    { name: 'Education', percentage: 15, color: '#047857', description: 'Admissions, teacher absenteeism, midday meals' },
    { name: 'Jobs & Employment', percentage: 10, color: '#9333EA', description: 'Benefits, application errors, training programs' },
    { name: 'Document Services', percentage: 20, color: '#0891B2', description: 'Aadhaar, PAN, biometric issues, certifications' }
  ];

  const flowSteps = [
    { icon: MessageSquare, title: 'Public Query Submission', description: 'Via Email, Website, WhatsApp, Phone, or Walk-in' },
    { icon: UserCheck, title: 'Automatic Ticket Creation', description: 'Unique ID generated for tracking' },
    { icon: ArrowRight, title: 'Ticket Assignment', description: 'Routed to appropriate staff/govt. liaison' },
    { icon: Phone, title: 'Issue Resolution', description: 'Follow-up with relevant departments' },
    { icon: Mail, title: 'Closure & Feedback', description: 'Ticket closed, feedback collected' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1667564790635-0f560121359e)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-semibold mb-6 leading-tight">
            Complaint Resolution System
          </h1>
          <p className="text-2xl md:text-3xl mb-4">
            Organize. Resolve. Impress.
          </p>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-8 text-[#1E3A8A] text-center">
            The Challenge
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-xl leading-relaxed text-[#374151] mb-6">
              Managing public queries and complaints manually is chaotic. Emails get lost, phone calls go unanswered, and constituents feel ignored. Without a systematic approach, even the most responsive public servant appears ineffective.
            </p>
            <p className="text-xl leading-relaxed text-[#374151]">
              The result? Frustrated constituents, overwhelmed staff, and a reputation for being unresponsive—even when you're working hard to help.
            </p>
          </div>
        </div>
      </section>

      {/* Solution - Flow Chart */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-4 text-[#1E3A8A] text-center">
            Our Systematic Solution
          </h2>
          <p className="text-xl text-center mb-12 text-[#374151] max-w-3xl mx-auto">
            A comprehensive ticket management system that brings order to chaos and turns complaints into opportunities for connection.
          </p>
          
          {/* Desktop Flow */}
          <div className="hidden lg:block relative">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-[#E5E7EB] transform -translate-y-1/2" />
            <div className="grid grid-cols-5 gap-4 relative">
              {flowSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-full bg-[#1E3A8A] flex items-center justify-center mb-4 relative z-10 shadow-lg">
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-[#1E3A8A]">{step.title}</h3>
                    <p className="text-sm text-[#374151]">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Flow */}
          <div className="lg:hidden space-y-6">
            {flowSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="flex gap-4 items-start">
                  <div className="w-16 h-16 rounded-full bg-[#1E3A8A] flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2 text-[#1E3A8A]">{step.title}</h3>
                    <p className="text-base text-[#374151]">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ticket Breakdown */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-[#1E3A8A] text-center">
            Understanding Your Constituency's Concerns
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Visual representation */}
            <div>
              <div className="space-y-4">
                {ticketCategories.map((category, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-2">
                      <span className="font-semibold text-[#1E3A8A]">{category.name}</span>
                      <span className="font-semibold text-[#374151]">{category.percentage}%</span>
                    </div>
                    <div className="w-full h-8 bg-[#E5E7EB] rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${category.percentage}%`,
                          backgroundColor: category.color
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Details */}
            <div className="space-y-6">
              {ticketCategories.map((category, index) => (
                <div key={index} className="p-6 border-2 border-[#E5E7EB] rounded-lg hover:border-[#EA580C] transition-colors duration-300">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-4 h-4 rounded-full flex-shrink-0 mt-1"
                      style={{ backgroundColor: category.color }}
                    />
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-[#1E3A8A]">{category.name}</h3>
                      <p className="text-[#374151]">{category.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-[#1E3A8A] text-center">
            Platform Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Multi-Channel Intake</h3>
              <p className="text-[#374151] leading-relaxed">
                Accept queries through email, web forms, WhatsApp, phone calls, and walk-ins—all automatically converted to trackable tickets.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Smart Assignment</h3>
              <p className="text-[#374151] leading-relaxed">
                Tickets are automatically routed to the right staff member or government liaison based on category and priority.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Real-Time Dashboard</h3>
              <p className="text-[#374151] leading-relaxed">
                View open tickets, resolution times, satisfaction scores, and trending issues at a glance.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Automated Updates</h3>
              <p className="text-[#374151] leading-relaxed">
                Constituents receive automatic SMS/email updates at each stage, keeping them informed without manual effort.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Feedback Collection</h3>
              <p className="text-[#374151] leading-relaxed">
                After resolution, automatic feedback requests help you measure satisfaction and identify areas for improvement.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Analytics & Reports</h3>
              <p className="text-[#374151] leading-relaxed">
                Monthly reports on resolution times, common issues, and staff performance help you continuously improve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-semibold mb-6">
            Transform Chaos into Order
          </h2>
          <p className="text-xl mb-8">
            Start resolving complaints systematically and building your reputation as a responsive public servant.
          </p>
          <Button 
            size="lg"
            className="bg-[#EA580C] hover:bg-[#DC4D09] text-white px-12 py-6 text-xl"
            asChild
          >
            <Link to="/contact">Get Started Today</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CRSPage;